import React, { useEffect, useState } from "react";
import { C } from "../constants.js";
import { I } from "../icons.jsx";
import { apiFetch } from "../api.js";
import { Spinner } from "../components/Spinner.jsx";
import { SweepStat } from "./_sweep_stat.jsx";

export function PipelineDashboardPage({ onMatterClick, showNotification }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [tierFilter, setTierFilter] = useState("all"); // all|active|idle|aging|stale
  const [attorneyFilter, setAttorneyFilter] = useState("all");
  const [stageFilter, setStageFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("staleness"); // staleness|client|activity|stage

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const result = await apiFetch("/api/dashboard/pipeline");
      setData(result);
    } catch (e) {
      showNotification && showNotification(`Failed to load pipeline: ${e.message}`, "error");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); }, []);

  const tierStyle = (tier) => {
    const map = {
      active: { bg: C.successBg, fg: C.success, label: "Active" },
      idle:   { bg: "#F2F2F2", fg: C.textMuted, label: "Idle" },
      aging:  { bg: `${C.gold}25`, fg: C.goldDim, label: "Aging" },
      stale:  { bg: C.errorBg, fg: C.error, label: "Stale" },
    };
    return map[tier] || map.idle;
  };

  // Filtering + sorting
  const filteredRows = data ? data.matters
    .filter(m => tierFilter === "all" || m.tier === tierFilter)
    .filter(m => attorneyFilter === "all" || (m.responsible_attorney || "(unassigned)") === attorneyFilter)
    .filter(m => stageFilter === "all" || (m.stage || "(unassigned)") === stageFilter)
    .filter(m => {
      if (!search) return true;
      const q = search.toLowerCase();
      return (m.client_name || "").toLowerCase().includes(q)
          || (m.display_number || "").toLowerCase().includes(q);
    })
    .sort((a, b) => {
      if (sortBy === "client") return (a.client_name || "").localeCompare(b.client_name || "");
      if (sortBy === "stage") return (a.stage || "").localeCompare(b.stage || "");
      if (sortBy === "activity") return (b.days_idle || 0) - (a.days_idle || 0);
      // default — staleness: stale first
      const tierOrder = { stale: 0, aging: 1, idle: 2, active: 3 };
      const ta = tierOrder[a.tier] ?? 4, tb = tierOrder[b.tier] ?? 4;
      if (ta !== tb) return ta - tb;
      return (b.days_idle || 0) - (a.days_idle || 0);
    })
    : [];

  if (loading) {
    return React.createElement("div", { style: { padding: "32px 40px", textAlign: "center", color: C.textMuted } },
      React.createElement(Spinner, { size: 28 }),
      React.createElement("div", { style: { fontSize: "14px", marginTop: "12px" } }, "Loading pipeline..."),
    );
  }

  if (!data) {
    return React.createElement("div", { style: { padding: "32px 40px", color: C.textMuted } },
      "Pipeline unavailable.");
  }

  const { summary, distributions, thresholds_days } = data;

  return React.createElement("div", { className: "fade-in", style: { padding: "32px 40px", maxWidth: "1400px" } },
    // Header
    React.createElement("div", { style: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "20px", flexWrap: "wrap", gap: "12px" } },
      React.createElement("div", null,
        React.createElement("h1", {
          style: { fontFamily: "'Playfair Display',serif", fontSize: "28px", fontWeight: 600, color: C.navy, margin: 0 },
        }, "Pipeline"),
        React.createElement("div", { style: { fontSize: "13px", color: C.textMuted, marginTop: "4px" } },
          `Every open matter, ranked by staleness · As of ${data.as_of ? new Date(data.as_of).toLocaleString() : "—"}`),
      ),
      React.createElement("button", {
        onClick: () => load(true), disabled: refreshing,
        style: { padding: "8px 14px", borderRadius: "8px", border: `1px solid ${C.warmGray}`, background: "#fff", color: C.navy, fontSize: "13px", cursor: refreshing ? "wait" : "pointer", display: "flex", alignItems: "center", gap: "6px" },
      }, refreshing ? React.createElement(Spinner, { size: 12 }) : I.refresh,
         refreshing ? "Refreshing..." : "Refresh"),
    ),

    // Summary tiles
    React.createElement("div", {
      style: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: "12px", marginBottom: "20px" },
    },
      React.createElement(SweepStat, { label: "Open Matters", value: summary.total_open, color: C.navy }),
      React.createElement(SweepStat, { label: "Active", value: summary.active, color: C.success }),
      React.createElement(SweepStat, { label: "Idle", value: summary.idle, color: C.textMuted }),
      React.createElement(SweepStat, { label: "Aging", value: summary.aging, color: C.goldDim }),
      React.createElement(SweepStat, { label: "Stale", value: summary.stale, color: summary.stale > 0 ? C.error : C.textMuted }),
    ),

    // Thresholds explainer (small, muted)
    React.createElement("div", { style: { fontSize: "11px", color: C.textLight, marginBottom: "16px", lineHeight: 1.5 } },
      `Tiers: Active ≤ ${thresholds_days.active_max} days · `
      + `Idle ${thresholds_days.active_max + 1}–${thresholds_days.idle_max} days · `
      + `Aging ${thresholds_days.idle_max + 1}–${thresholds_days.aging_max} days · `
      + `Stale > ${thresholds_days.aging_max} days. `
      + `Staleness measured from the most recent logged activity, or from open date if there's been no activity yet.`),

    // Filter row
    React.createElement("div", {
      style: { background: "#fff", border: `1px solid ${C.warmGray}`, borderRadius: "12px", padding: "16px 20px", marginBottom: "16px" },
    },
      // Search + tier chips
      React.createElement("div", { style: { display: "flex", gap: "12px", alignItems: "center", marginBottom: "12px", flexWrap: "wrap" } },
        React.createElement("div", { style: { position: "relative", flex: "1 1 240px", minWidth: "200px" } },
          React.createElement("span", { style: { position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: C.textLight } }, I.search),
          React.createElement("input", {
            type: "text", placeholder: "Search by client or matter #...",
            value: search, onChange: e => setSearch(e.target.value),
            style: { width: "100%", padding: "8px 12px 8px 38px", borderRadius: "8px", border: `1px solid ${C.warmGray}`, fontSize: "13px" },
          }),
        ),
        // Tier chips
        ["all", "stale", "aging", "idle", "active"].map(t => {
          const count = t === "all" ? summary.total_open : summary[t];
          const isActive = tierFilter === t;
          const s = t === "all" ? { bg: C.navy, fg: "#fff" } : tierStyle(t);
          return React.createElement("button", {
            key: t, onClick: () => setTierFilter(t),
            style: {
              padding: "6px 12px", borderRadius: "16px", fontSize: "12px", fontWeight: 600,
              border: `1px solid ${isActive ? (t === "all" ? C.navy : s.fg) : C.warmGray}`,
              background: isActive ? (t === "all" ? C.navy : s.bg) : "#fff",
              color: isActive ? (t === "all" ? "#fff" : s.fg) : C.textDark,
              cursor: "pointer",
            },
          }, `${t === "all" ? "All" : s.label} (${count})`);
        }),
      ),

      // Attorney + Stage + Sort dropdowns
      React.createElement("div", { style: { display: "flex", gap: "12px", flexWrap: "wrap", alignItems: "center" } },
        React.createElement("label", { style: { fontSize: "11px", color: C.textMuted, fontWeight: 600, textTransform: "uppercase" } }, "Attorney:"),
        React.createElement("select", {
          value: attorneyFilter, onChange: e => setAttorneyFilter(e.target.value),
          style: { padding: "6px 10px", borderRadius: "6px", border: `1px solid ${C.warmGray}`, fontSize: "12px", background: "#fff" },
        },
          React.createElement("option", { value: "all" }, `All (${summary.total_open})`),
          distributions.by_attorney.map(a =>
            React.createElement("option", { key: a.name, value: a.name }, `${a.name} (${a.count})`)),
        ),
        React.createElement("label", { style: { fontSize: "11px", color: C.textMuted, fontWeight: 600, textTransform: "uppercase" } }, "Stage:"),
        React.createElement("select", {
          value: stageFilter, onChange: e => setStageFilter(e.target.value),
          style: { padding: "6px 10px", borderRadius: "6px", border: `1px solid ${C.warmGray}`, fontSize: "12px", background: "#fff" },
        },
          React.createElement("option", { value: "all" }, `All (${summary.total_open})`),
          distributions.by_stage.map(s =>
            React.createElement("option", { key: s.name, value: s.name }, `${s.name} (${s.count})`)),
        ),
        React.createElement("label", { style: { fontSize: "11px", color: C.textMuted, fontWeight: 600, textTransform: "uppercase", marginLeft: "12px" } }, "Sort:"),
        React.createElement("select", {
          value: sortBy, onChange: e => setSortBy(e.target.value),
          style: { padding: "6px 10px", borderRadius: "6px", border: `1px solid ${C.warmGray}`, fontSize: "12px", background: "#fff" },
        },
          React.createElement("option", { value: "staleness" }, "Staleness (stale first)"),
          React.createElement("option", { value: "client" }, "Client name (A→Z)"),
          React.createElement("option", { value: "activity" }, "Days idle (most first)"),
          React.createElement("option", { value: "stage" }, "Stage"),
        ),
      ),
    ),

    // Results count
    React.createElement("div", { style: { fontSize: "12px", color: C.textMuted, marginBottom: "8px" } },
      `Showing ${filteredRows.length} of ${summary.total_open} open matters`),

    // Table
    React.createElement("div", {
      style: { background: "#fff", border: `1px solid ${C.warmGray}`, borderRadius: "12px", overflow: "hidden" },
    },
      filteredRows.length === 0
        ? React.createElement("div", { style: { padding: "40px", textAlign: "center", color: C.textMuted, fontSize: "13px" } },
            "No matters match the current filters.")
        : React.createElement("table", { style: { width: "100%", borderCollapse: "collapse" } },
          React.createElement("thead", null,
            React.createElement("tr", { style: { background: C.warmWhite } },
              ["Client", "Matter #", "Stage", "Attorney", "Last Activity", "Days Idle", "Status"].map((h, i) =>
                React.createElement("th", {
                  key: i,
                  style: { padding: "10px 16px", textAlign: "left", color: C.textMuted, fontWeight: 600, fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.04em" },
                }, h),
              ),
            ),
          ),
          React.createElement("tbody", null,
            ...filteredRows.map(m => {
              const s = tierStyle(m.tier);
              return React.createElement("tr", {
                key: m.matter_id,
                onClick: () => onMatterClick && onMatterClick({ id: m.matter_id, client: { name: m.client_name }, display_number: m.display_number }),
                style: {
                  borderTop: `1px solid ${C.warmGray}40`,
                  cursor: "pointer", transition: "background 0.15s",
                  background: m.tier === "stale" ? `${C.errorBg}40` : "#fff",
                },
                onMouseEnter: e => { e.currentTarget.style.background = C.warmWhite; },
                onMouseLeave: e => { e.currentTarget.style.background = m.tier === "stale" ? `${C.errorBg}40` : "#fff"; },
              },
                React.createElement("td", { style: { padding: "12px 16px", color: C.navy, fontWeight: 500 } },
                  m.client_name || "(no client)"),
                React.createElement("td", { style: { padding: "12px 16px", color: C.textMuted, fontFamily: "monospace", fontSize: "12px" } },
                  m.display_number || "—"),
                React.createElement("td", { style: { padding: "12px 16px", color: C.textDark, fontSize: "12px" } },
                  m.stage || React.createElement("span", { style: { color: C.textLight } }, "(no stage)")),
                React.createElement("td", { style: { padding: "12px 16px", color: C.textDark, fontSize: "12px" } },
                  m.responsible_attorney || React.createElement("span", { style: { color: C.textLight } }, "(unassigned)")),
                React.createElement("td", { style: { padding: "12px 16px", color: C.textMuted, fontSize: "12px" } },
                  m.last_activity_at
                    ? React.createElement(React.Fragment, null,
                        React.createElement("div", { style: { color: C.textDark } }, new Date(m.last_activity_at).toLocaleDateString()),
                        m.last_event_type && React.createElement("div", { style: { fontSize: "10px", color: C.textLight, marginTop: "2px" } },
                          m.last_event_type.replace(/_/g, " ")),
                      )
                    : React.createElement("span", { style: { fontStyle: "italic", color: C.textLight } },
                        `No activity · opened ${m.days_open}d ago`),
                ),
                React.createElement("td", { style: { padding: "12px 16px", color: C.textDark, fontSize: "13px", fontWeight: 500 } },
                  `${m.days_idle}d`),
                React.createElement("td", { style: { padding: "12px 16px" } },
                  React.createElement("span", {
                    style: { display: "inline-block", padding: "3px 10px", borderRadius: "10px", background: s.bg, color: s.fg, fontSize: "11px", fontWeight: 600 },
                  }, s.label),
                ),
              );
            }),
          ),
        ),
    ),
  );
}


